import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/StatCard";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AddSuretyLead } from "@/components/AddSuretyLead";
import { DncStatus, DncScrubButton } from "@/components/DncScrub";
import { SuretyLeadImport } from "@/components/SuretyLeadImport";
import { SuretyLeadActions } from "@/components/SuretyLeadActions";
import { Switch } from "@/components/ui/switch";

const tone = { sent: "bg-emerald-100 text-emerald-800", failed: "bg-red-100 text-red-800", paid: "bg-emerald-100 text-emerald-800" };

const PayDialog = ({ l, onClose, onDone }) => {
  const [amount, setAmount] = useState("");
  const [desc, setDesc] = useState("");
  const send = async () => {
    try { await api.post(`/surety/leads/${l.id}/payment-link`, { amount: Number(amount), description: desc, origin_url: window.location.origin }); toast.success(`Payment link emailed to ${l.email}`); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="surety-pay-dialog">
        <DialogHeader><DialogTitle>Email a bond payment link to {l.name}</DialogTitle></DialogHeader>
        <div><Label className="text-xs">Bond description</Label><Input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="e.g. Orange County contractor license bond" data-testid="surety-pay-desc" /></div>
        <div><Label className="text-xs">Amount ($)</Label><Input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="surety-pay-amount" /></div>
        <p className="text-xs text-slate-500">A secure Stripe checkout link is emailed to {l.email}. It expires in 23 hours.</p>
        <DialogFooter><Button onClick={send} disabled={!amount || desc.trim().length < 3} data-testid="surety-pay-send">Email payment link</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const day = (s) => (s ? new Date(s).toLocaleDateString() : "");
const BondInfo = ({ l }) => {
  const b = l.bond_info;
  let text;
  if (!l.license) text = "No license number - bond can't be checked";
  else if (!b) text = "Not checked yet - press Refresh bonds";
  else if (!b.found) text = `Not in the state licensee data yet (checked ${day(b.checked_at)})`;
  else if (b.bonded) text = `Bond on public record${b.bond_amount ? `: $${Number(b.bond_amount).toLocaleString()}` : ""}${b.bond_effective_date ? ` since ${day(b.bond_effective_date)}` : ""}`;
  else text = `No bond on public record${b.bond_cancelled_date ? ` (last bond cancelled ${day(b.bond_cancelled_date)})` : ""}`;
  const meta = b?.found ? [b.status && `license ${b.status}`, b.has_workers_comp != null && `workers' comp ${b.has_workers_comp ? "yes" : "no"}`,
    b.source_updated_at && `state data ${day(b.source_updated_at)}`, `checked ${day(b.checked_at)}`].filter(Boolean).join(" · ") : "";
  return <div className="text-xs text-slate-600" data-testid={`surety-lead-bond-info-${l.id}`}><b>Bond:</b> {text}{meta && <span className="text-slate-400"> · {meta}</span>}</div>;
};

const Lead = ({ l, onPay, onChanged, bondOn }) => (
  <div className="grid gap-2 border-t border-slate-100 py-3 text-sm md:grid-cols-[2fr_2fr_1fr_auto]" data-testid={`surety-lead-${l.id}`}>
    <div><div className="font-semibold text-slate-900">{l.name}</div><div className="text-xs text-slate-500">{l.business || "—"} · {l.license_type} {l.license}</div>
      <div className="text-xs text-slate-500">Licensed {l.licensed_on || "—"} · expires {l.expires || "—"}</div>{bondOn && <BondInfo l={l} />}
      {l.phone && <DncStatus l={l} testId={`surety-lead-dnc-${l.id}`} />}</div>
    <div className="text-xs text-slate-600"><div>{l.street}, {l.city}, {l.state} {l.zip}</div><div>{l.email || "No email found"}{l.phone ? ` · ${l.phone}` : ""}</div></div>
    <div className="flex flex-wrap gap-1">
      {l.prospect === "exam" && (l.licensed_since_exam
        ? <Badge className="border-0 bg-emerald-100 text-emerald-800" data-testid={`surety-lead-licensed-since-exam-${l.id}`}>Licensed since exam</Badge>
        : <Badge className="border-0 bg-amber-100 text-amber-800" data-testid={`surety-lead-exam-pending-${l.id}`}>Exam applicant</Badge>)}
      {l.bonded && <Badge className="border-0 bg-slate-800 text-white" data-testid={`surety-lead-bonded-${l.id}`}>Already bonded{l.bond_amount ? ` $${Number(l.bond_amount).toLocaleString()}` : ""}</Badge>}
      {l.email_status && <Badge className={`border-0 ${tone[l.email_status]}`}>Email {l.email_status}</Badge>}
      {l.postcard_status && <Badge className={`border-0 ${tone[l.postcard_status]}`}>Postcard {l.postcard_status}</Badge>}
      {l.call_status && <Badge variant="outline" data-testid={`surety-lead-call-${l.id}`}>Call: {l.call_status}</Badge>}
      {(l.dnc || l.not_interested) && <Badge className="border-0 bg-slate-200 text-slate-700">{l.dnc ? "On DNC list" : "Not interested"}</Badge>}
      {l.bond_payment && <Badge className={`border-0 ${tone[l.bond_payment.status] || "bg-sky-100 text-sky-800"}`} data-testid={`surety-lead-pay-status-${l.id}`}>Bond ${l.bond_payment.amount} {l.bond_payment.status}</Badge>}
    </div>
    <div className="text-xs text-slate-500 md:max-w-xs">
      {l.appointment && <div className="font-semibold text-emerald-700">Appt: {l.appointment.appointment_type} {l.appointment.appointment_time} {l.appointment.appointment_address}</div>}
      {l.last_summary ? <div>{l.last_summary}</div> : l.email_subject ? `“${l.email_subject}”` : ""}
      {l.last_recording && <audio controls preload="none" src={l.last_recording} className="mt-1 h-8 w-full" data-testid={`surety-lead-recording-${l.id}`} />}
      {l.email && <Button size="sm" variant="outline" className="mt-1 h-7" onClick={() => onPay(l)} data-testid={`surety-lead-paylink-${l.id}`}>Email payment link</Button>}
    </div>
    <div className="md:col-span-4"><SuretyLeadActions l={l} onChanged={onChanged} /></div>
  </div>
);

export default function SuretyLeads() {
  const [params] = useSearchParams();
  const [leads, setLeads] = useState(null);
  const [pay, setPay] = useState(null);
  const [hideBonded, setHideBonded] = useState(true);
  const [bondOn, setBondOn] = useState(false);
  useEffect(() => { api.get("/surety/options").then((r) => setBondOn(!!r.data.bond_check_ready)).catch(() => {}); }, []);
  const load = () => api.get("/surety/leads", { params: { run_id: params.get("run") || "" } }).then((r) => setLeads(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [params]); // eslint-disable-line react-hooks/exhaustive-deps
  const refresh = async () => {
    try { const { data } = await api.post("/surety/leads/refresh"); toast.success(`Updating ${data.leads} leads (${data.lookups} need a phone/email lookup). Check back in a few minutes.`); }
    catch (e) { toast.error(apiError(e)); }
  };
  const refreshBonds = async () => {
    try { const { data } = await api.post("/surety/leads/refresh-bonds"); toast.success(`Checking ${data.checking} licensed leads for a new bond. Anyone bonded is marked and Ava stops contacting them.`); setTimeout(load, 15000); }
    catch (e) { toast.error(apiError(e)); }
  };
  const shown = leads?.filter((l) => !(bondOn && hideBonded && l.bonded));
  const contactAll = async () => {
    if (!window.confirm("Email and mail a postcard to every contractor who hasn't received one yet? Postcards are billed per card.")) return;
    try { const { data } = await api.post("/surety/leads/contact-all"); toast.success(`Sending ${data.emails} emails and ${data.postcards} postcards`); setTimeout(load, 8000); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div data-testid="surety-leads-page">
      <PageHeader title="Contractor Leads" subtitle="Florida licensed contractors from your lead runs." />
      <div className="mb-4 flex flex-wrap gap-2">
        <AddSuretyLead onDone={load} />
        <SuretyLeadImport onDone={load} />
        <Button variant="outline" onClick={refresh} data-testid="surety-leads-refresh">Update data</Button>
        <DncScrubButton path="/surety/leads/dnc-scrub" onDone={load} testId="surety-leads-dnc-scrub" />
        {bondOn && <Button variant="outline" onClick={refreshBonds} data-testid="surety-leads-refresh-bonds">Refresh bonds</Button>}
        <Button onClick={contactAll} data-testid="surety-leads-contact-all">Email &amp; postcard everyone</Button>
        {bondOn && <label className="flex items-center gap-2 text-sm"><Switch checked={hideBonded} onCheckedChange={setHideBonded} data-testid="surety-leads-hide-bonded" />Hide already-bonded</label>}
      </div>
      <Card className="p-6">
        {leads && !leads.length && <p className="text-sm text-slate-500" data-testid="surety-leads-empty">No leads yet - start a lead run, add one manually or upload a spreadsheet.</p>}
        {shown?.map((l) => <Lead key={l.id} l={l} onPay={setPay} onChanged={load} bondOn={bondOn} />)}
      </Card>
      {pay && <PayDialog l={pay} onClose={() => setPay(null)} onDone={() => { setPay(null); load(); }} />}
    </div>
  );
}
